// zip ファイルの生成と展開を行うユーティリティ。
// マスターCSVのエクスポート/インポートで、複数のCSVファイルをひとまとめに扱うために使う。
// ブラウザ上で動く前提のため、Node.js 固有の API には依存しない。入出力はすべてバイト列(Blob)で受け渡す。
import JSZip from "jszip";
import { isCsvFileName } from "./fileNameExtensions";
import { errorMessageOf } from "./typed";

export type ZipEntry = {
  fileName: string;
  text: string;
};

export type ZipLimits = {
  // 受け取る zip ファイルそのものの大きさの上限(バイト)。zip を開く前に判定する。
  maxFileByteSize: number;
  // zip に含めてよいファイルエントリ数の上限。フォルダのエントリは数えない。
  maxEntryCount: number;
  // 展開したCSV本文をすべて合わせた文字数の上限。
  maxTotalTextLength: number;
};

// 上限は「意図しない巨大ファイルで画面が固まるのを防ぐ」ためのもの。
// そのため、いずれの上限も重い処理を始める前か、処理を進めながら判定する。
export const DEFAULT_ZIP_LIMITS: ZipLimits = {
  maxFileByteSize: 20 * 1024 * 1024,
  maxEntryCount: 100,
  maxTotalTextLength: 50 * 1024 * 1024,
};

// バイト数を利用者に伝わる単位に直す。1メガバイトは1024かける1024バイトとして数える。
function megabyteTextOf(byteSize: number): string {
  const megabytes = byteSize / (1024 * 1024);
  return megabytes.toFixed(1);
}

// 文字列のエントリ群をそのまま格納した zip を作る。
// 文字コードの変換や BOM の付け外しは行わず、渡された文字列をそのままファイル本文として書き込む。
export async function zipOfTextEntries(entries: readonly ZipEntry[]): Promise<Blob> {
  const zip = new JSZip();
  for (const entry of entries) {
    zip.file(entry.fileName, entry.text);
  }
  const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  return zipBlob;
}

export type ZipReadResult =
  | { ok: true; entries: ZipEntry[] }
  | { ok: false; errors: string[] };

// zip からCSVファイルだけを取り出す。
// サブディレクトリの中も走査し、返すファイル名はディレクトリを除いた部分にする。拡張子の大文字小文字は区別しない。
// 壊れたzip、CSVが0件、ファイル名の重複、ファイルの大きさ・件数・文字数の上限超過は、それぞれ日本語のメッセージを添えたエラーにする。
// 1つでも違反があれば ok: false を返し、一部だけを取り込んだ成功は返さない。
// 上限の判定は「zip を開く前の大きさ」「走査しながらの件数」「展開しながらの文字数」の順に置き、
// 超えた時点で打ち切る。判定を最後にまとめると、防ぎたかった重い処理が先に終わってしまうため。
export async function textEntriesOfZip(file: Blob, limits?: Partial<ZipLimits>): Promise<ZipReadResult> {
  const effectiveLimits: ZipLimits = { ...DEFAULT_ZIP_LIMITS, ...limits };
  if (file.size > effectiveLimits.maxFileByteSize) {
    // zip を開く前に弾く。展開してから判定したのでは、止めたかった重い処理を先に走らせてしまうため。
    const limitText = megabyteTextOf(effectiveLimits.maxFileByteSize);
    const actualText = megabyteTextOf(file.size);
    const overSizeMessage = `zip ファイルの大きさが上限(${limitText}メガバイト)を超えています。選ばれたファイルは${actualText}メガバイトです。ファイルを分けてください。`;
    return { ok: false, errors: [overSizeMessage] };
  }
  const zip = await loadZipSafely(file);
  if (zip === null) {
    return { ok: false, errors: ["zip ファイルの形式が正しくないため展開できませんでした。"] };
  }

  const scanned = collectFileEntriesWithinLimit(zip, effectiveLimits.maxEntryCount);
  if (scanned.overLimit) {
    const overEntryCountMessage = `zip 内のファイル数が上限(${effectiveLimits.maxEntryCount}件)を超えています。${scanned.handles.length}件目を見つけた時点で読み取りを中断しました。ファイルを分けてください。`;
    return { ok: false, errors: [overEntryCountMessage] };
  }

  const csvFileEntries = scanned.handles.filter((fileEntry) => isCsvFileName(fileEntry.baseName));
  const read = await readZipEntries(csvFileEntries, effectiveLimits.maxTotalTextLength);
  if (read.overTextLength) {
    const overTextLengthMessage = `展開後の合計文字数が上限(${effectiveLimits.maxTotalTextLength}文字)を超えています。${read.totalTextLength}文字まで読んだ時点で中断したため、残りのファイルは展開していません。ファイルを分けてください。`;
    return { ok: false, errors: [overTextLengthMessage] };
  }

  const errors: string[] = [...read.readErrors, ...duplicateFileNameErrors(read.entries)];
  if (read.entries.length === 0) {
    errors.push("zip 内に csv ファイルが1件も見つかりませんでした。");
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, entries: read.entries };
}

// zip の展開そのものに失敗した場合(壊れたzip、zipでないデータなど)は null を返す。
async function loadZipSafely(file: Blob): Promise<JSZip | null> {
  try {
    const zip = await JSZip.loadAsync(file);
    return zip;
  } catch {
    return null;
  }
}

type FileEntryHandle = {
  baseName: string;
  zipObject: JSZip.JSZipObject;
};

// zip 内のファイルエントリ(フォルダを除く)を、ディレクトリを除いたファイル名付きで集める。
// 集めながら件数を数え、上限を超えた時点でそこから先は見に行かない。
// overLimit が true のとき、handles の件数は「上限を超えた時点の件数」であって zip 全体の件数ではない。
function collectFileEntriesWithinLimit(zip: JSZip, maxEntryCount: number): { handles: FileEntryHandle[]; overLimit: boolean } {
  const entryPaths = Object.keys(zip.files);
  const handles: FileEntryHandle[] = [];
  let index = 0;
  let overLimit = false;
  while (index < entryPaths.length && !overLimit) {
    const entryPath = entryPaths[index] ?? "";
    const zipObject = zip.files[entryPath];
    const isFileEntry = zipObject !== undefined && !zipObject.dir;
    if (isFileEntry) {
      handles.push({ baseName: baseNameOf(entryPath), zipObject });
      overLimit = handles.length > maxEntryCount;
    }
    index += 1;
  }
  return { handles, overLimit };
}

// zip 内のパスからディレクトリ部分を除いたファイル名を取り出す。区切り文字はスラッシュとバックスラッシュの両方を対象にする。
function baseNameOf(entryPath: string): string {
  const segments = entryPath.split(/[\\/]/);
  const lastSegment = segments[segments.length - 1];
  if (lastSegment !== undefined && lastSegment.length > 0) {
    return lastSegment;
  }
  return entryPath;
}

type ZipEntriesReadResult = {
  entries: ZipEntry[];
  readErrors: string[];
  // 合計文字数の上限を超えたため、残りのエントリを展開せずに打ち切ったかどうか。
  overTextLength: boolean;
  // 打ち切った場合は、そこまでに読み終えた分の合計文字数。
  totalTextLength: number;
};

// 対象のファイルエントリを順番に読み込む。個別のエントリが読み込めなかった場合は、そのエントリ名を含むエラーとして扱う。
// 1つ展開するたびに合計文字数を積み上げ、上限を超えた時点で残りには手を付けない。
// 全部展開してから合計を見ると、止めたかった重い展開処理が先に終わってしまうため。
async function readZipEntries(fileEntries: readonly FileEntryHandle[], maxTotalTextLength: number): Promise<ZipEntriesReadResult> {
  const entries: ZipEntry[] = [];
  const readErrors: string[] = [];
  let index = 0;
  let totalTextLength = 0;
  let overTextLength = false;
  while (index < fileEntries.length && !overTextLength) {
    const fileEntry = fileEntries[index];
    if (fileEntry !== undefined) {
      const readResult = await readSingleZipEntry(fileEntry);
      if (readResult.ok) {
        entries.push(readResult.entry);
        totalTextLength += readResult.entry.text.length;
        overTextLength = totalTextLength > maxTotalTextLength;
      } else {
        readErrors.push(readResult.error);
      }
    }
    index += 1;
  }
  return { entries, readErrors, overTextLength, totalTextLength };
}

async function readSingleZipEntry(fileEntry: FileEntryHandle): Promise<{ ok: true; entry: ZipEntry } | { ok: false; error: string }> {
  try {
    const text = await fileEntry.zipObject.async("string");
    return { ok: true, entry: { fileName: fileEntry.baseName, text } };
  } catch (error) {
    const reason = errorMessageOf(error);
    return { ok: false, error: `${fileEntry.baseName} の読み込みに失敗しました(${reason})。` };
  }
}

// 展開したファイル名の重複を検出する。同じファイル名が複数のCSVファイルから見つかった場合にエラーを返す。
function duplicateFileNameErrors(entries: readonly ZipEntry[]): string[] {
  const seenNames = new Set<string>();
  const duplicateNames = new Set<string>();
  for (const entry of entries) {
    if (seenNames.has(entry.fileName)) {
      duplicateNames.add(entry.fileName);
    }
    seenNames.add(entry.fileName);
  }
  return Array.from(duplicateNames).map((name) => `ファイル名 ${name} が複数のCSVファイルで重複しています。`);
}
